/**
 * analytics.js
 * Centralised GA4 + Google Ads + Meta Pixel tracking for the Career Analysis tool.
 *
 * Events fired:
 *   page_view           — landing page load
 *   form_fill           — user clicks "Unlock My Report" (ContactForm submit)
 */

// ─── IDs from environment ────────────────────────────────────────────────────
const GA_ID    = process.env.REACT_APP_GA_ID;
const GADS_ID  = process.env.REACT_APP_GADS_ID;
const GADS_LBL = process.env.REACT_APP_GADS_LABEL;
const META_ID  = process.env.REACT_APP_META_PIXEL_ID;

let _initialized = false;

// ─── INIT (call once, early in app) ─────────────────────────────────────────
export function initTracking() {
  if (_initialized) return;
  _initialized = true;

  _initGA();
  _initMeta();
}

// ─── GA4 + Google Ads via gtag.js ─────────────────────────────────────────
function _initGA() {
  if (!GA_ID) return;

  // Inject gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // IMPORTANT: must use traditional function + arguments (not arrow/spread)
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());

  // GA4 config — disable auto page_view (we fire it manually)
  window.gtag('config', GA_ID, { send_page_view: false });

  // Google Ads config (reuses same gtag.js)
  if (GADS_ID) {
    window.gtag('config', GADS_ID);
  }
}

// ─── Meta Pixel ──────────────────────────────────────────────────────────────
function _initMeta() {
  if (!META_ID || window.fbq) return;

  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){
      n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments);
    };
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', META_ID);
}

// ─── TRACK: Landing page view ────────────────────────────────────────────────
export function trackLandingPageView() {
  // GA4
  if (window.gtag && GA_ID) {
    window.gtag('event', 'page_view', {
      page_title: 'Career Analysis - Landing',
      page_path: '/',
    });
  }
  // Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
}

// ─── TRACK: Form fill / conversion ("Unlock My Report" clicked) ──────────────
export function trackFormFill({ email, phone, current_ctc } = {}) {
  const eventId = _uuid(); // shared ID across all platforms for deduplication

  // GA4 — custom event
  if (window.gtag && GA_ID) {
    window.gtag('event', 'form_fill', {
      event_category: 'conversion',
      event_label: 'unlock_my_report',
      transaction_id: eventId,
    });
  }

  // Google Ads — conversion
  if (window.gtag && GADS_ID && GADS_LBL) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_ID}/${GADS_LBL}`,
      transaction_id: eventId,   // deduplication
    });
  }

  // Meta Pixel — Lead event (standard event for form fills)
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'unlock_my_report',
      eventID: eventId,           // deduplication with CAPI
    });
  }

  return eventId; // caller can log or send to backend if needed
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function _uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
